/* eslint-disable no-underscore-dangle */
import {useMutation} from '@apollo/client';
import Button from '@material-ui/core/Button';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Delete from '@material-ui/icons/Delete';
import Skeleton from '@material-ui/lab/Skeleton/Skeleton';
import {Group, Tournament} from 'packages/common-types/schema-types';
import {GROUP_HEADERS} from 'packages/constants/table-constants';
import ConfirmDeleteGroup from 'packages/dialogs/ConfirmDeleteGroup';
import {DELETE_GROUP} from 'packages/graphql/v2/mutations';
import {useModal} from 'packages/hooks/use-modal';
import useTablePagination from 'packages/hooks/use-table-pagination';
import BaseTable from 'packages/tables/BaseTable';
import {useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import Swal from 'sweetalert2';

type DeleteGroupVars = {
  tournamentId: string;
  groupNumber: number;
};

type DeleteGroupData = {
  tournamentId: string;
};

type Props = {
  tournament: Tournament;
  dataGroup: Group[];
  refetchGroup: () => void;
};

const GroupsTable = (props: Props) => {
  const {tournament, dataGroup, refetchGroup} = props;
  const {toggle, modalOpen} = useModal();
  const [group, setGroup] = useState<Group | null>(null);
  const isAvailable = !(tournament.status === 'No iniciado');
  const [deleteGroup, {loading: loadingMutation}] = useMutation<
    DeleteGroupData,
    DeleteGroupVars
  >(DELETE_GROUP);

  const {
    onChangePage: onChangePagination,
    onChangeRowsPerPage,
    page,
    rowsPerPage,
  } = useTablePagination();

  if (!dataGroup) {
    return (
      <>
        <div style={{marginTop: 30}}>
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
        </div>
      </>
    );
  }

  const handleConfirmDelete = (group: Group) => {
    setGroup(group);
    toggle();
  };

  const handleDeleteGroup = async () => {
    try {
      await deleteGroup({
        variables: {
          tournamentId: tournament._id,
          groupNumber: group?.groupNumber,
        },
      });
      refetchGroup();
      toggle();
      Swal.fire({
        title: 'Success',
        text: 'Grupo eliminado correctamente',
        icon: 'success',
      });
    } catch (error) {
      console.error('ERROR => ', error);
      toggle();
      Swal.fire({
        title: 'Error',
        text: 'Hubo un error al eliminar un grupo',
        icon: 'error',
      });
    }
  };

  return (
    <>
      <ConfirmDeleteGroup
        onDeleteGroupConfirm={handleDeleteGroup}
        groupNumber={group?.groupNumber}
        members={group?.members}
        toggle={toggle}
        modalOpen={modalOpen}
        loading={loadingMutation}
      />
      <BaseTable headers={GROUP_HEADERS}>
        {dataGroup
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((group) => (
            <TableRow key={uuidv4()}>
              <TableCell component="th" scope="row">
                {group.groupNumber + 1}
              </TableCell>
              <TableCell align="right">
                {group.members.map((member) => member.fullName).join(',')}
              </TableCell>
              <TableCell align="right">
                <Button
                  onClick={() => handleConfirmDelete(group)}
                  size="small"
                  variant="text"
                  startIcon={<Delete />}
                  disabled={isAvailable}
                >
                  Eliminar
                </Button>
              </TableCell>
            </TableRow>
          ))}
      </BaseTable>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={dataGroup ? dataGroup?.length : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePagination}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </>
  );
};

export default GroupsTable;
